import { html, nothing } from 'lit';
import { property } from 'lit/decorators.js';
import { localized, msg } from '@lit/localize';
import { pdsCustomElement as customElement } from '../../decorators/pds-custom-element';
import { PdsElement } from '../PdsElement';
import styles from './alert.scss?inline';
import '../button/button';
import '../layout-container/layout-container';
import '@principal/design-system-icons-web/check';
import '@principal/design-system-icons-web/alert-circle';
import '@principal/design-system-icons-web/alert-triangle';
import '@principal/design-system-icons-web/x';
import '@principal/design-system-icons-web/info';
import { PdsLink } from '../link/link';

type LayoutContainerOptions = { variant: string; removePadding?: string };

/**
 * @summary This component provides feedback to the user about the state of their interaction with in a page or application.
 *
 * @slot default The contents of the alert
 *
 * @fires pds-button-click A custom event dispatched on a click on the alert dismiss button
 */
@customElement('pds-alert', {
  category: 'component',
  type: 'component',
  styles,
})
@localized()
export class PdsAlert extends PdsElement {
  connectedCallback() {
    super.connectedCallback();
    this.setLocale();
  }

  /**
   * Style variant
   * - **information** default, renders the alert used for informational alerts
   * - **success** renders the alert used for success alerts
   * - **error** renders the alert used for error alerts, is not dismissible
   * - **warning** renders the alert used for warning alerts
   * - **banner** renders the alert used for banner alerts, by default includes a layout-container
   */
  @property()
  variant: 'information' | 'success' | 'error' | 'warning' | 'banner' =
    'information';

  /**
   * Add role for a11y
   * - **alert** provides alert role to denote important, and usually time-sensitive, information which will be used by the browser to send out an accessible alert event to assistive technology products which can then notify the user
   */
  @property({ reflect: true })
  role: 'alert';

  /**
   * Place alert in a layout-container
   * - Value should be an object passing layout container props, for example {variant: 'default', removePadding: 'all'}
   * - First value should be the layout-container variant, second value (optional) should be the removePadding property
   */
  @property()
  layoutContainer?: LayoutContainerOptions | string;

  /**
   * Hide dismiss button
   */
  @property({ type: Boolean })
  hideDismissButton: boolean = false;

  /**
   * Show or hide alert
   */
  @property()
  isAlertShown: boolean = true;

  /**
   * @internal
   */
  @property()
  layoutContainerObj: LayoutContainerOptions = { variant: 'default' };

  /**
   * @internal
   *
   * Checks markup for errors and adds browser console warnings for misconfigurations
   */
  preflight() {
    if (this.variant === 'error' && this.hideDismissButton) {
      console.warn(
        'Error alert cannot be dismissible and will always render without a close button. Please remove the hideDismissButton property to remove this warning.',
        this.outerHTML,
      );
    }

    const links = this.querySelectorAll('pds-link');
    links.forEach((link) => {
      // link must be handled as a PdsLink in order to appease Typescript
      const pdsLink = link as PdsLink;
      if (
        this.variant === 'banner' &&
        pdsLink.variant !== 'emphasis-inverted'
      ) {
        console.warn(
          `Links in a banner alert must use the 'emphasis-inverted' variant, '${pdsLink.variant}' was supplied. Link variant has been modified on the page. Update this link with the correct variant to remove this warning.`,
          link,
        );
      } else if (this.variant !== 'banner' && pdsLink.variant !== 'emphasis') {
        console.warn(
          `Links in a non-banner alert must use the 'emphasis' variant, '${pdsLink.variant}' was supplied. Link variant has been modified on the page. Update this link with the correct variant to remove this warning.`,
          link,
        );
      }
    });
  }

  /**
   * @internal
   *
   * @returns icon for the alert
   */
  getIcon() {
    switch (this.variant) {
      case 'success':
        return html`<pds-icon-check size="lg"></pds-icon-check>`;
      case 'information':
        return html`<pds-icon-info size="lg"></pds-icon-info>`;
      case 'warning':
        return html`<pds-icon-alert-triangle
          size="lg"
        ></pds-icon-alert-triangle>`;
      default:
        return html`<pds-icon-alert-circle size="lg"></pds-icon-alert-circle>`;
    }
  }

  /**
   * @internal
   */
  private handleClick() {
    this.isAlertShown = false;
  }

  /**
   * @internal
   *
   * @returns close button markup for dismissable alerts
   */
  getCloseButtonMarkup() {
    return html`
      <div class=${this.classEl('close-button-wrapper')}>
        <pds-button
          class=${this.classEl('close-button')}
          variant="${this.variant === 'banner' ? 'icon-inverted' : 'icon'}"
          size="sm"
          ariaLabel="${msg('close')}"
          @click=${this.handleClick}
          ><pds-icon-x class=${this.classEl('close-button-x-icon')}></pds-icon-x
        ></pds-button>
      </div>
    `;
  }

  /**
   * @internal
   */
  get classNames() {
    return {
      [this.variant]: !!this.variant,
    };
  }

  render() {
    if (this.isAlertShown) {
      this.preflight();

      // Set default layout container from object for banner if one is not passed in
      if (this.variant === 'banner' && !this.layoutContainer) {
        this.layoutContainer = this.layoutContainerObj;
      }

      // create a layoutContainerObj from passed in string
      if (
        typeof this.layoutContainer === 'string' &&
        this.layoutContainer.substring(0, 1) === '{'
      ) {
        const replaceQuotes = this.layoutContainer.replace(/'/g, '"');
        this.layoutContainerObj = JSON.parse(replaceQuotes);
      }

      // update link variants if they do not match expected design
      const links = this.querySelectorAll('pds-link');
      links.forEach((link) => {
        if (
          this.variant === 'banner' &&
          link.getAttribute('variant') !== 'emphasis-inverted'
        ) {
          link.setAttribute('variant', 'emphasis-inverted');
        } else if (
          this.variant !== 'banner' &&
          link.getAttribute('variant') !== 'emphasis'
        ) {
          link.setAttribute('variant', 'emphasis');
        }
      });

      if (this.layoutContainer && this.variant !== 'banner') {
        return html`<pds-layout-container
          variant=${this.layoutContainerObj.variant}
          size=${this.layoutContainerObj.removePadding}
          ><div class=${this.getClass()}>
            <div class="${this.classEl('card')}">
              <div class="${this.classEl('alert-body')}">
                <div class="${this.classEl('icon')}">${this.getIcon()}</div>
                <div class="${this.classEl('alert-content')}">
                  <slot></slot>
                </div>
                ${this.variant === 'error' || this.hideDismissButton
                  ? nothing
                  : this.getCloseButtonMarkup()}
              </div>
            </div>
          </div></pds-layout-container
        >`;
      }
      if (this.layoutContainer && this.variant === 'banner') {
        return html`<div class=${this.getClass()}>
          <pds-layout-container
            variant=${this.layoutContainerObj.variant}
            size=${this.layoutContainerObj.removePadding}
          >
            <div class="${this.classEl('card')}">
              <div class="${this.classEl('alert-body')}">
                <div class="${this.classEl('icon')}">${this.getIcon()}</div>
                <div class="${this.classEl('alert-content')}">
                  <slot></slot>
                </div>
                ${this.hideDismissButton
                  ? nothing
                  : this.getCloseButtonMarkup()}
              </div>
            </div>
          </pds-layout-container>
        </div>`;
      }
      return html`<div class=${this.getClass()}>
        <div class="${this.classEl('card')}">
          <div class="${this.classEl('alert-body')}">
            <div class="${this.classEl('icon')}">${this.getIcon()}</div>
            <div class="${this.classEl('alert-content')}">
              <slot></slot>
            </div>
            ${this.variant === 'error' || this.hideDismissButton
              ? nothing
              : this.getCloseButtonMarkup()}
          </div>
        </div>
      </div>`;
    }
    return html``;
  }
}
